import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { scrambleText, separateWordParts } from './scrambleUtils';
import { gameLevels, Level } from './config/levels';
import { featureFlags } from './config/featureFlags';
import { getAndCacheDailyLevels } from './services/levelService';

interface GameContextType {
  originalSentence: string;
  originalWords: string[];
  scrambledWords: string[];
  solvedIndices: number[];
  isGameActive: boolean;
  currentLevel: number;
  totalLevels: number;
  levelData: Level | null;
  isUsingDailyLevels: boolean;
  loadingDailyLevels: boolean;
  dailyLevelsError: string | null;
  startGame: () => void;
  startSpecificLevel: (levelId: number) => void;
  solveWord: (index: number) => void;
  resetGame: () => void;
  advanceToNextLevel: () => void;
  reshuffleWords: () => void;
  reshuffleWord: (index: number) => void;
  isLevelComplete: () => boolean;
  isGameComplete: () => boolean;
  refreshDailyLevels: () => Promise<void>;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [originalSentence, setOriginalSentence] = useState('');
  const [originalWords, setOriginalWords] = useState<string[]>([]);
  const [scrambledWords, setScrambledWords] = useState<string[]>([]);
  const [solvedIndices, setSolvedIndices] = useState<number[]>([]);
  const [isGameActive, setIsGameActive] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [levelData, setLevelData] = useState<Level | null>(null);
  const [levels, setLevels] = useState<Level[]>([]);
  
  // States for API integration
  const [isUsingDailyLevels, setIsUsingDailyLevels] = useState(false);
  const [loadingDailyLevels, setLoadingDailyLevels] = useState(featureFlags.useRemoteLevels);
  const [dailyLevelsError, setDailyLevelsError] = useState<string | null>(null);
  
  const [totalLevels, setTotalLevels] = useState(0);

  // Performance analytics tracking
  const trackLevelStart = (levelId: number, isDailyLevel: boolean) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'level_start', {
        level_id: levelId,
        is_daily: isDailyLevel
      });
    }
  };

  // Function to load levels from API or fallback
  const loadLevels = async () => {
    // Reset states
    setDailyLevelsError(null);
    
    // If remote levels feature is enabled
    if (featureFlags.useRemoteLevels) {
      setLoadingDailyLevels(true);
      
      try {
        console.log('Fetching daily levels from API...');
        // Clear session storage cache to ensure fresh data
        const today = new Date().toISOString().split('T')[0];
        const cachedKey = `daily_levels_${today}`;
        sessionStorage.removeItem(cachedKey);
        
        const dailyLevels = await getAndCacheDailyLevels();
        
        if (dailyLevels.length === 0) {
          throw new Error('No levels received from API');
        }
        
        // Check if these are from the API or the fallback
        const isFromApi = dailyLevels !== gameLevels;
        
        setLevels(dailyLevels);
        setTotalLevels(dailyLevels.length);
        setIsUsingDailyLevels(isFromApi);
        
        if (isFromApi) {
          console.log(`Successfully loaded ${dailyLevels.length} daily levels from API`);
          
          // Report successful loading to analytics
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'daily_levels_loaded', {
              count: dailyLevels.length
            });
          }
        } else {
          console.log('Using fallback levels from local data');
          setDailyLevelsError('Unable to load today\'s challenges. Using default levels instead.');
          
          // Log fallback to analytics
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'error', {
              error_type: 'api_failure',
              error_message: 'Fallback to local levels'
            });
          }
        }
      } catch (error) {
        console.error('Failed to load levels:', error);
        
        // Set appropriate error message for user
        let errorMessage = 'Unable to load today\'s challenges. Using default levels instead.';
        
        if (error instanceof Error) {
          if (error.message.includes('timeout') || error.message.includes('NetworkError')) {
            errorMessage = 'Network error while loading challenges. Check your connection and try again.';
          }
          
          // Log error to analytics
          if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'error', {
              error_type: 'api_failure',
              error_message: error.message
            });
          }
        }
        
        setDailyLevelsError(errorMessage);
        
        // The service should have already fallen back to local levels, but just in case:
        setLevels(gameLevels);
        setTotalLevels(gameLevels.length);
        setIsUsingDailyLevels(false);
      } finally {
        setLoadingDailyLevels(false);
      }
    } else {
      // Use local levels when feature flag is off
      console.log('Using local levels (feature flag off)');
      setLevels(gameLevels);
      setTotalLevels(gameLevels.length);
      setIsUsingDailyLevels(false);
    }
  };
  
  // Public method to refresh daily levels from API
  const refreshDailyLevels = async () => {
    if (!isGameActive) {
      // Track refresh action for analytics
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'refresh_daily_levels');
      }
      
      await loadLevels();
    }
  };
  
  // Load levels based on feature flag configuration
  useEffect(() => {
    loadLevels();
  }, []);

  const setupLevel = (levelId: number) => {
    const level = levels.find(level => level.id === levelId);
    
    if (!level) {
      console.error(`Level ${levelId} not found`);
      return false;
    }
    
    const sentence = level.sentence;
    const words = sentence.split(' ');
    const scrambled = scrambleText(sentence).split(' ');
    
    setLevelData(level);
    setOriginalSentence(sentence);
    setOriginalWords(words);
    setScrambledWords(scrambled);
    setSolvedIndices([]);
    setCurrentLevel(levelId);
    setIsGameActive(true);
    
    // Track level start for analytics
    trackLevelStart(levelId, isUsingDailyLevels);
    
    return true;
  };

  const startGame = () => {
    setupLevel(1);
  };

  const startSpecificLevel = (levelId: number) => {
    setupLevel(levelId);
  };

  const solveWord = (index: number) => {
    if (index >= 0 && index < originalWords.length && !solvedIndices.includes(index)) {
      setSolvedIndices(prev => {
        const newSolved = [...prev, index];
        
        // Track progress for analytics
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'word_solved', {
            level_id: currentLevel,
            progress: `${newSolved.length}/${originalWords.length}`,
            is_daily: isUsingDailyLevels
          });
        }
        
        return newSolved;
      });
    }
  };

  const resetGame = () => {
    setOriginalSentence('');
    setOriginalWords([]);
    setScrambledWords([]);
    setSolvedIndices([]);
    setIsGameActive(false);
    setCurrentLevel(1);
    setLevelData(null);
  };

  const advanceToNextLevel = () => {
    // Track level completion
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'level_complete', {
        level_id: currentLevel,
        is_daily: isUsingDailyLevels
      });
    }
    
    if (currentLevel < totalLevels) {
      setupLevel(currentLevel + 1);
    } else {
      // Game complete!
      setIsGameActive(false);
      
      // Track game completion
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'game_complete', {
          levels_completed: totalLevels,
          is_daily: isUsingDailyLevels
        });
      }
    }
  };

  const isLevelComplete = () => {
    return solvedIndices.length === originalWords.length && originalWords.length > 0;
  };

  const isGameComplete = () => {
    return isLevelComplete() && currentLevel === totalLevels;
  };
  
  // Utility function to scramble a single word
  const scrambleWord = (word: string): string => {
    if (word.length <= 1) return word.toUpperCase();
    
    // Separate alphanumeric characters from punctuation
    const { chars } = separateWordParts(word);
    
    // If no alphanumeric characters, return empty string
    if (chars.length === 0) return '';
    
    // Fisher-Yates shuffle algorithm for alphanumeric characters only
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    
    return chars.join('').toUpperCase();
  };
  
  // Reshuffle all unsolved words
  const reshuffleWords = () => {
    // Create a new scrambled version of unsolved words
    const newScrambledWords = [...scrambledWords];
    
    // Only reshuffle words that are not yet solved
    originalWords.forEach((word, index) => {
      if (!solvedIndices.includes(index)) {
        // Scramble this word again
        newScrambledWords[index] = scrambleWord(word);
      }
    });
    
    // Update state with the new scrambled words
    setScrambledWords(newScrambledWords);
    
    // Track reshuffling for analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'reshuffle', {
        level_id: currentLevel,
        progress: `${solvedIndices.length}/${originalWords.length}`,
        is_daily: isUsingDailyLevels
      });
    }
  };
  
  // Reshuffle a single word at the given index
  const reshuffleWord = (index: number) => {
    // Skip if the word is already solved or index is invalid
    if (solvedIndices.includes(index) || index < 0 || index >= originalWords.length) {
      return;
    }
    
    // Create a new scrambled words array
    const newScrambledWords = [...scrambledWords];
    
    // Scramble just the specified word
    newScrambledWords[index] = scrambleWord(originalWords[index]);
    
    // Update state with the new scrambled words
    setScrambledWords(newScrambledWords);
    
    // Track individual word reshuffling for analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'reshuffle_word', {
        level_id: currentLevel,
        word_index: index,
        is_daily: isUsingDailyLevels
      });
    }
  };

  return (
    <GameContext.Provider
      value={{
        originalSentence,
        originalWords,
        scrambledWords,
        solvedIndices,
        isGameActive,
        currentLevel,
        totalLevels,
        levelData,
        isUsingDailyLevels,
        loadingDailyLevels,
        dailyLevelsError,
        startGame,
        startSpecificLevel,
        solveWord,
        resetGame,
        advanceToNextLevel,
        reshuffleWords,
        reshuffleWord,
        isLevelComplete,
        isGameComplete,
        refreshDailyLevels,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGameContext = (): GameContextType => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};

// Add TypeScript type declaration for gtag
declare global {
  interface Window {
    gtag: (
      command: string,
      action: string,
      params?: Record<string, any>
    ) => void;
  }
}